import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, Inject, Injectable } from '@angular/core';
import { first, Observable } from 'rxjs';
import { APP_CONFIG } from '../../../shared/configurations/configurations.factory';
import { Configurations } from '../../../shared/configurations/models/configurations.model';
import { AreaDto } from '../DTOs/area.dto';
import { ApiEnumerationModel } from '../DTOs/base-DTOs/api-enumeration-model.dto';
import { ApiModel } from '../DTOs/base-DTOs/api-model.dto';
import { ApiObjectModel } from '../DTOs/base-DTOs/api-object-model.dto';
import { CreateOrUpdateUserPropertyDto, UpdateUserPropertyDto, UserPropertyCreatedDto, UserPropertyReplacedDto } from '../DTOs/properties.dto';
import { UserChangeRequestDto, UserChangeResponseDto, UserDto, UserPropertiesContainerDto, UserSummaryDto } from '../DTOs/user.dto';

type Scope = 'PersonalHierarchy' | 'Hierarchy' | 'All'
type ViewModel = 'Head' | 'Full'

@Injectable({
  providedIn: 'root',
  deps: [APP_INITIALIZER, APP_CONFIG]
})
export class UserProxyService {

  private readonly baseUrl: string

  constructor(@Inject(APP_CONFIG) config: Configurations, private readonly httpClient: HttpClient) {
    this.baseUrl = config.environmentConfig.lobbyBaseUrl + '/user'
  }

  getCurrentUser$(): Observable<ApiObjectModel<UserDto>> {
    return this.httpClient.get<ApiObjectModel<UserDto>>(this.baseUrl + `/me`)
  }

  getUserInfo$(id: number): Observable<ApiObjectModel<UserDto>> {
    return this.httpClient.get<ApiObjectModel<UserDto>>(this.baseUrl + `/${id}`)
  }

  searchUsers$(pageIndex: number | undefined = undefined,
    pageSize: number | undefined = undefined,
    scope: Scope | undefined = undefined,
    areaId: string | undefined = undefined,
    name: string | undefined = undefined,
    email: string | undefined = undefined,
    role: string | undefined = undefined,
    isInterdicred: boolean | undefined = undefined,
    isAvaible: boolean | undefined = undefined,
    omitRequester: boolean | undefined = undefined,
    viewModel: ViewModel | undefined = undefined,
    sort: 'UserId' | 'Nome' | 'Cognome' | 'Email' | 'Created' | 'UserId-' | 'Nome-' | 'Cognome-' | 'Email-' | 'Created-' | undefined | null = undefined,

  ): Observable<ApiEnumerationModel<UserSummaryDto | UserDto>> {

    let query: string = '';

    if (pageIndex) query = query + `${query ? '&' : ''}pg_ix=${pageIndex}`
    if (pageSize) query = query + `${query ? '&' : ''}pg_sz=${pageSize}`
    if (scope) query = query + `${query ? '&' : ''}scope=${scope}`
    if (areaId) query = query + `${query ? '&' : ''}areaId=${areaId}`
    if (omitRequester !== undefined) query = query + `${query ? '&' : ''}omitRequester=${omitRequester}`
    if (name) query = query + `${query ? '&' : ''}name=${name}`
    if (email) query = query + `${query ? '&' : ''}email=${email}`
    if (role) query = query + `${query ? '&' : ''}role=${role}`
    if (viewModel) query = query + `${query ? '&' : ''}vw=${viewModel}`
    if (isInterdicred) query = query + `${query ? '&' : ''}Interdicted=${isInterdicred}`
    if (isAvaible) query = query + `${query ? '&' : ''}Available=${isAvaible}`
    if (sort) query = query + `${query ? '&' : ''}s=${sort}`

    return this.httpClient.get<ApiEnumerationModel<UserSummaryDto | UserDto>>(this.baseUrl + `${query ? '?' + query : ''}`)
  }

  getCurrentUserProperties$(): Observable<ApiObjectModel<UserPropertiesContainerDto>> {
    return this.httpClient.get<ApiObjectModel<UserPropertiesContainerDto>>(this.baseUrl + '/me/properties')
  }

  setNewUserProperty$(userId: number, version: number, propertyName: string, propertyValue: string): Observable<ApiObjectModel<UserPropertyCreatedDto>> {

    const dto = { name: propertyName, value: propertyValue } as CreateOrUpdateUserPropertyDto

    return this.httpClient.post<ApiObjectModel<UserPropertyCreatedDto>>(this.baseUrl + `/${userId}/properties?ver=${version}`, dto)
  }

  setUserProperty$(userId: number, version: number, propertyName: string, propertyValue: string): Observable<ApiObjectModel<UserPropertyReplacedDto>> {

    const dto = { value: propertyValue } as UpdateUserPropertyDto

    return this.httpClient.put<ApiObjectModel<UserPropertyReplacedDto>>(this.baseUrl + `/${userId}/properties/${propertyName}?ver=${version}`, dto)
  }

  setProperties$(userId: number, version: number, creates: [string, string][], changes: [string, string][], removes: string[]): Observable<ApiObjectModel<UserChangeResponseDto>> {
    const dto = {
      properties: {
        creates: creates.map(c => { return { name: c[0], value: c[1] } }),
        changes: changes.map(c => { return { name: c[0], value: c[1] } }),
        removes: removes
      }
    } as UserChangeRequestDto

    return this.httpClient.patch<ApiObjectModel<UserChangeResponseDto>>(this.baseUrl + `/${userId}?ver=${version}`, dto)
  }

  getUserAreas$() {
    return this.httpClient.get<ApiEnumerationModel<AreaDto>>(this.baseUrl + '/me/area')
  }

  setUserPermission$(userId: number, permission: string, version: number): Observable<ApiModel> {
    return this.httpClient.post<ApiModel>(this.baseUrl + `/${userId}/permissions/${permission}?ver=${version}`, null)
  }

  removeUserPermission$(userId: number, permission: string, version: number): Observable<ApiModel> {
    return this.httpClient.delete<ApiModel>(this.baseUrl + `/${userId}/permissions/${permission}?ver=${version}`)
  }

  removeUserInterdiction$(userId: number): Observable<ApiModel> {
    return this.httpClient.delete<ApiModel>(this.baseUrl + `/${userId}/interdictions`)
  }

  interdictUser$(userId: number): Observable<ApiModel> {
    return this.httpClient.post<ApiModel>(this.baseUrl + `/${userId}/interdict`, null)
  }

}
